import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { AuditStatus, SmartContractTemplate } from './useSmartContractTemplates';

// Re-export AuditStatus for convenience
export type { AuditStatus } from './useSmartContractTemplates';

export interface RegisterAuditDto {
  auditorCompany: string;
  auditDate: string;
  reportUrl: string;
  findings?: string;
  recommendations?: string;
}

export interface AuditFailedDto {
  reason: string;
  auditorCompany?: string;
  auditDate?: string;
}

export interface AuditDetails {
  status: AuditStatus;
  auditorCompany: string | null;
  auditDate: string | null;
  reportUrl: string | null;
  findings: string | null;
  codeIntegrity: boolean;
}

export interface AuditRecord {
  id: string;
  templateId: string;
  version: string;
  auditStatus: AuditStatus;
  auditorCompany: string | null;
  auditDate: string | null;
  codeHash: string;
  createdAt: string;
}

// ==================== AUDIT LIST TYPES ====================

export interface AuditListFilters {
  status?: AuditStatus;
  auditorCompany?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'auditDate' | 'name' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface AuditListItem {
  id: string;
  name: string;
  description?: string;
  tokenStandard: string;
  auditStatus: AuditStatus;
  auditorCompany?: string;
  auditDate?: string;
  auditReportUrl?: string;
  auditFindings?: string;
  currentVersion: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedAuditsResponse {
  data: AuditListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AuditStats {
  byStatus: {
    PENDING_AUDIT: number;
    AUDITED: number;
    AUDIT_FAILED: number;
  };
  pendingCount: number;
  totalTemplates: number;
  recentAudits: AuditListItem[];
  byAuditor: Array<{
    auditorCompany: string;
    count: number;
  }>;
}

// API Functions
const registerAudit = async ({ templateId, dto }: { templateId: string; dto: RegisterAuditDto }): Promise<SmartContractTemplate> => {
  const response = await api.post(`/tokenization/smart-contracts/templates/${templateId}/audit`, dto);
  return response.data;
};

const markAuditFailed = async ({ templateId, dto }: { templateId: string; dto: AuditFailedDto }): Promise<SmartContractTemplate> => {
  const response = await api.post(`/tokenization/smart-contracts/templates/${templateId}/audit/fail`, dto);
  return response.data;
};

const fetchAuditDetails = async (templateId: string): Promise<AuditDetails> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${templateId}/audit`);
  return response.data;
};

const fetchAuditHistory = async (templateId: string): Promise<AuditRecord[]> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${templateId}/audit/history`);
  return response.data;
};

// ==================== AUDIT LIST API FUNCTIONS ====================

const fetchAuditList = async (filters: AuditListFilters): Promise<PaginatedAuditsResponse> => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.auditorCompany) params.append('auditorCompany', filters.auditorCompany);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  
  const response = await api.get(`/tokenization/smart-contracts/audits?${params.toString()}`);
  return response.data;
};

const fetchAuditStats = async (): Promise<AuditStats> => {
  const response = await api.get('/tokenization/smart-contracts/audits/stats');
  return response.data;
};

// Hooks
export function useRegisterAudit() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: registerAudit,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['smart-contract-template', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['smart-contract-templates'] });
      queryClient.invalidateQueries({ queryKey: ['template-audit', variables.templateId] });
    },
  });
}

export function useMarkAuditFailed() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: markAuditFailed,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['smart-contract-template', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['smart-contract-templates'] });
      queryClient.invalidateQueries({ queryKey: ['template-audit', variables.templateId] });
    },
  });
}

export function useAuditDetails(templateId: string | undefined) {
  return useQuery({
    queryKey: ['template-audit', templateId],
    queryFn: () => fetchAuditDetails(templateId!),
    enabled: !!templateId,
  });
}

export function useAuditHistory(templateId: string | undefined) {
  return useQuery({
    queryKey: ['template-audit-history', templateId],
    queryFn: () => fetchAuditHistory(templateId!),
    enabled: !!templateId,
  });
}

// ==================== AUDIT LIST HOOKS ====================

export function useAuditList(filters: AuditListFilters = {}) {
  return useQuery({
    queryKey: ['audit-list', filters],
    queryFn: () => fetchAuditList(filters),
  });
}

export function useAuditStats() {
  return useQuery({
    queryKey: ['audit-stats'],
    queryFn: fetchAuditStats,
  });
}

// Export all hooks as a single object
export const useTemplateAudit = {
  useRegisterAudit,
  useMarkAuditFailed,
  useAuditDetails,
  useAuditHistory,
  useAuditList,
  useAuditStats,
};

export default useTemplateAudit;
